import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import { BillingService } from "../../BillingModule/services/billing";
import {
  IsLoadedClientInvoices,
  IsLoadingClientInvoices,
  loadClientInvoices,
  SelectClientInvoicesFilters,
  SelectClientInvoicesInfo,
  SelectOrderedBy,
  setIsLoadingClientInvoices,
} from "../redux/reducers/clientInvoices.reducer";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";

import { useClientsList } from "./useClientsList";

export const useClientInvoices = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);
  const isLoadingClientInvoices = useSelector(IsLoadingClientInvoices);
  const isLoadedClientInvoices = useSelector(IsLoadedClientInvoices);
  const clientInvoicesFilters = useSelector(SelectClientInvoicesFilters);
  const clientInvoicesInfo = useSelector(SelectClientInvoicesInfo);

  const { list: invoicesInfo } = useSelector(SelectOrderedBy);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  const loadClientInvoicesInfo = useCallback(
    async (clientId) => {
      if (rolesLoaded && isClientsLoaded) {
        // This check should go in all clients custom hooks
        if (!clientsIds.includes(clientId)) {
          dispatch(
            addToast({
              mode: "error",
              message: "You're not allowed to see this Client Page",
            })
          );
          navigate(ROUTE_FORBIDDEN_PAGE);
          return true;
        }
        // Load info header when user put a new URL
        if (
          !ClientForHeaderInfo ||
          (ClientForHeaderInfo && ClientForHeaderInfo.id !== clientId)
        ) {
          dispatch(setIsActualClientForHeaderInfoLoading(true));
          let headerInfo = await ClientsService.getClientModuleHeaderInfo(
            clientId
          )
            .then((response) => {
              if (!response.success) {
                dispatch(
                  addToast({
                    mode: "error",
                    message: response.message,
                  })
                );
                dispatch(setIsActualClientForHeaderInfoLoading(false));
                navigate(ROUTE_CLIENTS);
                return true;
              } else {
                return response.data;
              }
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message:
                    "Something wrong happened while getting the client header information.",
                })
              );
              dispatch(setIsActualClientForHeaderInfoLoading(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          dispatch(setActualClientForHeaderInfo(headerInfo));
        }
        // page info
        if (
          (!isLoadedClientInvoices ||
            clientInvoicesInfo?.account?.externalId !== clientId) &&
          clientInvoicesFilters.accountIds.length > 0
        ) {
          dispatch(setIsLoadingClientInvoices(true));
          const invoicesInfo = await BillingService.searchInvoices(
            clientInvoicesFilters
          )
            .then((data: any) => {
              return data;
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message: "Client not found",
                })
              );
              dispatch(setIsLoadingClientInvoices(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          dispatch(loadClientInvoices(invoicesInfo));
        }
      }
    },
    [dispatch, rolesLoaded, clientInvoicesFilters, isClientsLoaded]
  );

  useEffect(() => {
    loadClientInvoicesInfo(clientId);
  }, [loadClientInvoicesInfo]);

  return {
    isLoadingClientInvoices,
    invoicesInfo,
    clientInvoicesFilters,
    userRolesList,
    rolesLoaded,
  };
};
